import { type Immutable } from 'immer';
import { useEffect, useMemo, useState } from 'react';
import { getHorizonStartMs } from '../layout/getHorizonWindow';
import { type ServiceDisplayWidget, type WidgetHorizon } from '../layout/types';
import { useServiceDisplayDayParts } from './useServiceDisplayDayParts';

const TICK_MS = 60_000;

/**
 * Returns the epoch ms at which the configured horizon's time bucket starts,
 * or `null` for the `realtime` horizon (which uses active events, not a window).
 *
 * Re-evaluates every minute so the bucket rolls forward in real time (e.g. when
 * the local clock crosses the top of the hour, midnight, or a day-part boundary).
 */
export function useHorizonStartMs(
  horizon: WidgetHorizon,
  widget: Immutable<ServiceDisplayWidget>,
): number | null {
  const [tick, setTick] = useState(0);
  const { data: dayParts = [] } = useServiceDisplayDayParts();

  useEffect(() => {
    if (horizon === 'realtime') return;
    const interval = setInterval(() => setTick((t) => t + 1), TICK_MS);
    return () => clearInterval(interval);
  }, [horizon]);

  return useMemo(() => {
    // `tick` is in the dep list so we re-evaluate every minute.
    void tick;
    const now = Date.now();
    return getHorizonStartMs(horizon, {
      now,
      localNow: new Date(now),
      widgetThresholds: widget.thresholds ?? [],
      allDayParts: dayParts,
    });
  }, [horizon, widget.thresholds, dayParts, tick]);
}
